import { computed, ref, unref, isRef } from 'vue';

/**
 * Composable for handling pagination functionality
 *
 * @param {Object} options - Configuration options for pagination
 * @param {number|import('vue').Ref<number>} [options.pageSize=10] - Number of items per page or ref containing that value
 * @param {number|import('vue').Ref<number>} [options.total=0] - Total number of items or ref containing that value
 * @param {Function} [options.onPageChange] - Callback function triggered when page changes, receives object with current page
 */
export function usePagination(options = {}) {
  const currentPage = ref(1);
  // Use the provided ref or create a new one
  const pageSize = isRef(options.pageSize)
    ? options.pageSize
    : ref(options.pageSize || 10);
  const total = isRef(options.total) ? options.total : ref(options.total || 0);

  const totalPages = computed(() => Math.ceil(unref(total) / unref(pageSize)));
  const showingPage = computed(() => {
    const start = (currentPage.value - 1) * unref(pageSize) + 1;
    const end = Math.min(currentPage.value * unref(pageSize), unref(total));
    return `${start}-${end}`;
  });
  const isFirstPage = computed(() => currentPage.value === 1);
  const isLastPage = computed(() => currentPage.value === totalPages.value);

  const nextPage = () => {
    if (!isLastPage.value) {
      currentPage.value += 1;
      if (options.onPageChange)
        options.onPageChange({ page: currentPage.value });
    }
  };

  const previousPage = () => {
    if (!isFirstPage.value) {
      currentPage.value -= 1;
      if (options.onPageChange)
        options.onPageChange({ page: currentPage.value });
    }
  };

  const goToPage = page => {
    if (page >= 1 && page <= totalPages.value) {
      currentPage.value = page;
      if (options.onPageChange)
        options.onPageChange({ page: currentPage.value });
    }
  };

  const resetPage = () => {
    currentPage.value = 1;
    if (options.onPageChange) options.onPageChange({ page: currentPage.value });
  };

  return {
    currentPage,
    pageSize,
    total,
    totalPages,
    showingPage,
    isFirstPage,
    isLastPage,
    nextPage,
    previousPage,
    goToPage,
    resetPage,
  };
}
